import type { PostHog, PostHogInterface } from 'posthog-js';
import { useMemo } from 'react';

import {
    getRunEnvironment,
    getRuntimeConfig,
} from '@apps/web/lib/runtime_config';

const MAX_BUFFERED_CALLS = 1000;

type PostHogClient = PostHog;
type PostHogCall = (client: PostHogClient) => void;

let client: PostHogClient | null = null;
let initialization: Promise<void> | null = null;
let disabled = false;
const bufferedCalls: Array<PostHogCall> = [];

export function initializePostHog(): Promise<void> {
    if (initialization) {
        return initialization;
    }
    initialization = import('posthog-js')
        .then(
            ({ default: loadedClient }) =>
                new Promise<void>((resolve) => {
                    client = loadedClient;
                    const apiKey = getRuntimeConfig().posthogKey;
                    if (!apiKey) {
                        disabled = true;
                        bufferedCalls.length = 0;
                        resolve();
                        return;
                    }
                    const onLoaded = (initializedClient: PostHogInterface) => {
                        if (getRunEnvironment() === 'development') {
                            try {
                                initializedClient.debug();
                            } catch {
                                // Analytics must not affect application behavior.
                            }
                        }
                        flushBufferedCalls();
                        resolve();
                    };
                    if (loadedClient.__loaded) {
                        onLoaded(loadedClient);
                        return;
                    }
                    try {
                        loadedClient.init(apiKey, {
                            api_host: 'https://us.posthog.com',
                            loaded: onLoaded,
                            opt_out_capturing_by_default:
                                getRunEnvironment() === 'development',
                            sanitize_properties: sanitizePosthogProperties,
                        });
                    } catch {
                        disabled = true;
                        bufferedCalls.length = 0;
                        resolve();
                    }
                }),
        )
        .catch((error: unknown) => {
            disabled = true;
            bufferedCalls.length = 0;
            console.error('PostHog initialization failed', error);
        });
    return initialization;
}

function flushBufferedCalls(): void {
    if (!client || !client.__loaded) {
        return;
    }
    const calls = bufferedCalls.splice(0);
    for (const call of calls) {
        try {
            call(client);
        } catch {
            // A vendor failure must not drop later buffered events.
        }
    }
}

function enqueue(call: PostHogCall): void {
    if (disabled) {
        return;
    }
    if (client?.__loaded) {
        try {
            call(client);
        } catch {
            // Analytics must never break the caller.
        }
        return;
    }
    if (bufferedCalls.length < MAX_BUFFERED_CALLS) {
        bufferedCalls.push(call);
    }
    void initializePostHog();
}

export const posthog = {
    capture: (...args: Parameters<PostHogClient['capture']>) =>
        enqueue((client) => client.capture(...args)),
    captureAndFlush: async (
        ...args: Parameters<PostHogClient['capture']>
    ): Promise<void> => {
        enqueue((client) => client.capture(...args));
        await initializePostHog();
    },
    identify: (...args: Parameters<PostHogClient['identify']>) =>
        enqueue((client) => client.identify(...args)),
    setPersonProperties: (
        ...args: Parameters<PostHogClient['setPersonProperties']>
    ) => enqueue((client) => client.setPersonProperties(...args)),
    group: (...args: Parameters<PostHogClient['group']>) =>
        enqueue((client) => client.group(...args)),
};

export function usePostHog() {
    return useMemo(() => posthog, []);
}

const REDACTED_URL_QUERY_PARAMS = ['search', 'query', 'contactPhone'];

function scrubUrlQueryParams(value: unknown): unknown {
    if (
        typeof value !== 'string' ||
        (!value.includes('?') && !value.includes('#'))
    ) {
        return value;
    }
    let url: URL;
    let isAbsolute: boolean;
    try {
        url = new URL(value);
        isAbsolute = true;
    } catch {
        try {
            url = new URL(value, 'https://phoebe.invalid');
            isAbsolute = false;
        } catch {
            return value;
        }
    }
    let changed = false;
    if (
        url.hash.startsWith('#teamsConnect=') ||
        (url.pathname === '/slack-agent-v3/link' && url.hash.length > 0)
    ) {
        url.hash = '';
        changed = true;
    }
    for (const param of REDACTED_URL_QUERY_PARAMS) {
        if (url.searchParams.has(param)) {
            url.searchParams.set(param, 'redacted');
            changed = true;
        }
    }
    if (!changed) {
        return value;
    }
    return isAbsolute
        ? url.toString()
        : `${url.pathname}${url.search}${url.hash}`;
}

export function sanitizePosthogProperties(
    properties: Record<string, unknown>,
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [
            key,
            scrubUrlQueryParams(value),
        ]),
    );
}
